import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { organisationSettingsApi } from "../../api/organisationSettingsApi";
import type { OrganisationSettings } from "../../types/organizationSettings.types";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import i18n from "../../i18n";

interface OrganisationSettingsState {
  items: OrganisationSettings[];
  conf: any | null;
  isLoading: boolean;
  isConfLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  hasMore: boolean;
  pageIndex: number;
  totalCount: number;
  error: string | null;
}

const initialState: OrganisationSettingsState = {
  items: [],
  conf: null,
  isLoading: false,
  isConfLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,
  hasMore: true,
  pageIndex: 0,
  totalCount: 0,
  error: null,
};

const organisationSettingsSlice = createSlice({
  name: "organisationSettings",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setConfLoading: (state, action: PayloadAction<boolean>) => {
      state.isConfLoading = action.payload;
    },
    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },
    setOrganisationSettings: (
      state,
      action: PayloadAction<OrganisationSettings[]>,
    ) => {
      state.items = action.payload;
    },
    setOrganisationConf: (state, action: PayloadAction<any>) => {
      state.conf = action.payload;
    },
    appendOrganisationSettings: (
      state,
      action: PayloadAction<OrganisationSettings[]>,
    ) => {
      state.items = [...state.items, ...action.payload];
    },
    addSingleOrganisationSettingsLocally: (
      state,
      action: PayloadAction<OrganisationSettings>,
    ) => {
      state.items = [action.payload, ...state.items];
      state.totalCount += 1;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    resetOrganisationSettingsList: (state) => {
      state.items = [];
      state.conf = null;
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
    },
    updateSingleOrganisationSettings: (
      state,
      action: PayloadAction<Partial<OrganisationSettings> & { id: string }>,
    ) => {
      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
    removeSingleOrganisationSettings: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setConfLoading,
  setAdding,
  setEditing,
  setDeleting,
  setOrganisationSettings,
  setOrganisationConf,
  appendOrganisationSettings,
  addSingleOrganisationSettingsLocally,
  setTotalCount,
  setHasMore,
  setPageIndex,
  resetOrganisationSettingsList,
  updateSingleOrganisationSettings,
  removeSingleOrganisationSettings,
} = organisationSettingsSlice.actions;

// Async Thunks

export const fetchOrganisationSettings = (
  payload: {
    pageSize?: number;
    pageIndex?: number;
    orderBy?: string;
    sortDecending?: boolean;
    settingDefinitionId?: string;
    status?: string;
    groupIds?: string[];
    ids?: string[];
  } = {},
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().organisationSettings;

    const incomingPageIndex = payload.hasOwnProperty("pageIndex")
      ? payload.pageIndex!
      : state.pageIndex;

    if (state.isLoading || (!state.hasMore && incomingPageIndex !== 0)) return;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: payload.pageSize ?? 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy ?? "",
        sortDecending: false,
        groupIds: payload.groupIds ?? [],
        ids: payload.ids ?? [],

        ...(payload.status && {
          status: payload.status,
        }),
      };

      const response =
        await organisationSettingsApi.searchOrganisationSettings(requestBody);

      const fetchedData = response.data || [];
      const totalCount = response.count ?? response.totalCount ?? 0;

      dispatch(setTotalCount(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.items.length + fetchedData.length;

      dispatch(
        setHasMore(
          currentLoadedCount < totalCount &&
            fetchedData.length >= (payload.pageSize ?? 10),
        ),
      );

      if (incomingPageIndex === 0) {
        dispatch(setOrganisationSettings(fetchedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendOrganisationSettings(fetchedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Organisation Settings error:", error);

      toast.error(
        i18n.t("common:slices.organisationSettings.fetchError", {
          defaultValue: "Failed to fetch organisation settings",
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
};
export const fetchOrganisationConf = (organisationId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setConfLoading(true));
      const response =
        await organisationSettingsApi.getOrganisationConf(organisationId);
      const confData = response.data || response;
      dispatch(setOrganisationConf(confData));
      return confData;
    } catch (error) {
      console.error("Fetch Organisation Conf error:", error);
      toast.error(
        i18n.t("common:slices.organisationSettings.fetchConfError", {
          defaultValue: "Failed to fetch organisation configuration",
        }),
      );
      throw error;
    } finally {
      dispatch(setConfLoading(false));
    }
  };
};

export const addOrganisationSettings = (
  data: Partial<OrganisationSettings>,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAdding(true));

      const res =
        await organisationSettingsApi.createOrganisationSettings(data);
      const createdItem = res?.data || res;

      if (createdItem && createdItem.id) {
        dispatch(addSingleOrganisationSettingsLocally(createdItem));
      } else {
        dispatch(fetchOrganisationSettings({ pageIndex: 0 }));
      }

      toast.success(
        i18n.t("common:slices.organisationSettings.addSuccess", {
          defaultValue: "Organisation setting created successfully",
        }),
      );
      return createdItem;
    } catch (error) {
      console.error("Create Organisation Settings error:", error);
      toast.error(
        i18n.t("common:slices.organisationSettings.addError", {
          defaultValue: "Failed to create organisation setting",
        }),
      );
      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};

export interface BulkOrganisationSettingsPayload {
  providerId?: string;
  organisationSetting: Array<{
    settingDefinitionId?: string;
    organisationId: string;
    value: string;
    encryptedValue?: string;
    currentVersion?: number;
    status?: string;
    settingType?: number | null;
    moduleName?: number | null;
  }>;
}

export const addOrganisationSettingsBulk = (
  data: BulkOrganisationSettingsPayload,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAdding(true));

      const res =
        await organisationSettingsApi.bulkCreateOrganisationSettings(data);
      const responseData = res?.data || res;

      dispatch(fetchOrganisationSettings({ pageIndex: 0 }));

      toast.success(
        i18n.t("common:slices.organisationSettings.addManySuccess", {
          defaultValue: "Organisation settings created successfully",
        }),
      );
      return responseData;
    } catch (error) {
      console.error("Bulk Create Organisation Settings error:", error);
      toast.error(
        i18n.t("common:slices.organisationSettings.addManyError", {
          defaultValue: "Failed to create organisation settings",
        }),
      );
      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};

export const editOrganisationSettings = (
  data: Partial<OrganisationSettings> & { id: string },
) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setEditing(true));
      const res =
        await organisationSettingsApi.updateOrganisationSettings(data);
      const updatedItem = res?.data || data;

      dispatch(updateSingleOrganisationSettings(updatedItem));

      toast.success(
        i18n.t("common:slices.organisationSettings.editSuccess", {
          defaultValue: "Organisation setting updated successfully",
        }),
      );
      return updatedItem;
    } catch (error) {
      console.error("Edit Organisation Settings error:", error);
      toast.error(
        i18n.t("common:slices.organisationSettings.editError", {
          defaultValue: "Failed to update organisation setting",
        }),
      );
      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const removeOrganisationSettings = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setDeleting(true));

      dispatch(removeSingleOrganisationSettings(id));

      await organisationSettingsApi.removeOrganisationSettings(id);

      toast.success(
        i18n.t("common:slices.organisationSettings.removeSuccess", {
          defaultValue: "Organisation setting removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Organisation Settings error:", error);
      toast.error(
        i18n.t("common:slices.organisationSettings.removeError", {
          defaultValue: "Failed to remove organisation setting",
        }),
      );
      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default organisationSettingsSlice.reducer;
